// frontend/src/pages/PharmacistPatient.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header/Header';
import Background from '../components/Background/Background';
import '../styles/PharmacistPatient.css';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../api/fetchWithAuth';

type WorkOrderStatus =
  | 'PENDING'
  | 'PARTIALLY_APPROVED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'COMPLETED_EXTERNALLY';

type WorkOrderType = 'CHANGE' | 'REGISTRATION' | 'DISPENSING_STATUS';
type ItemAction = 'ADD' | 'EDIT' | 'REMOVE' | 'PAUSE' | 'RESUME';

type WorkOrder = {
  work_order_id: string;
  order_type: WorkOrderType;
  patient_id: string;
  pharmacy_id: string;
  status: WorkOrderStatus;
  created_at: string;
};

type WorkOrderItem = {
  item_id: string;
  action: ItemAction;
  medication_identifier: string;
  proposed_payload: any;
  review_id: string | null;
  reviewed_at: string | null;
  decision: 'APPROVE' | 'DENY' | null;
  reason_code: string | null;
  notesPlaintext?: string | null;
};

type WorkOrderResponse = {
  workOrder: WorkOrder;
  items: WorkOrderItem[];
};

type PatientDetailsOk = {
  patientId: string;
  ssn: string;
  name: string;
  pharmacy: string;
  nextDispensionDate?: string;
};

type PatientDetailsMaybe404 = {
  patientId?: string;
  ssn?: string;
  error?: string;
};

type DecisionState = 'approved' | 'rejected';

type RejectionReason = {
  reason_code: string;
  display_label: string;
  sort_order?: number;
};

// Fallback list (matches your INSERTs) if backend fetch fails for any reason
const FALLBACK_REASONS: RejectionReason[] = [
  { reason_code: 'PRESCRIPTION_MISSING', display_label: 'Vantar lyfseðil', sort_order: 10 },
  {
    reason_code: 'DOSE_WORRY',
    display_label: 'Áhyggjur af því að vitlaus skammtur hafi verið valinn',
    sort_order: 20,
  },
  {
    reason_code: 'MEDICATION_WORRY',
    display_label: 'Áhyggjur af því að vitlaust lyf hafi verið valið',
    sort_order: 30,
  },
  { reason_code: 'INTERACTION_RISK', display_label: 'Óæskilegar milliverkanir', sort_order: 40 },
  { reason_code: 'NOT_ELIGIBLE', display_label: 'Lyf hentar ekki lyfjaskömmtun', sort_order: 50 },
  {
    reason_code: 'MISMATCH',
    display_label: 'Ordination í kerfi passar ekki við lyfseðil sem var sendur',
    sort_order: 60,
  },
  { reason_code: 'OTHER', display_label: 'Annað (vinsamlegast útskýrðu)', sort_order: 999 },
];

export default function PharmacistPatient() {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const API_BASE = 'https://api-staging.mitoflux.is';

  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string>('');

  const [wo, setWo] = useState<WorkOrder | null>(null);
  const [items, setItems] = useState<WorkOrderItem[]>([]);
  const [patientName, setPatientName] = useState<string>('—');
  const [patientSsn, setPatientSsn] = useState<string>('—');
  const [patientPharmacy, setPatientPharmacy] = useState<string>('—');

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [decision, setDecision] = useState<Record<string, DecisionState | undefined>>({});
  const [submittingItemId, setSubmittingItemId] = useState<string | null>(null);

  // Reject modal state
  const [rejectingItemId, setRejectingItemId] = useState<string | null>(null);
  const [reasons, setReasons] = useState<RejectionReason[]>([]);
  const [rejectReasonCode, setRejectReasonCode] = useState<string>('');
  const [rejectNotes, setRejectNotes] = useState<string>('');

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!workOrderId) return;

      try {
        setLoading(true);
        setErrMsg('');

        // 1) Load work order + items
        const woRes = await fetchWithAuth(
          getAccessTokenSilently,
          `${API_BASE}/api/work-orders/${encodeURIComponent(workOrderId)}`
        );

        if (!woRes.ok) {
          const text = await woRes.text();
          throw new Error(`Work order API ${woRes.status}: ${text}`);
        }

        const woJson = (await woRes.json()) as WorkOrderResponse;
        if (!mounted) return;

        setWo(woJson.workOrder);
        setItems(woJson.items);

        // Pre-fill decisions from DB if reviews exist
        const initial: Record<string, DecisionState | undefined> = {};
        for (const it of woJson.items) {
          if (it.decision === 'APPROVE') initial[it.item_id] = 'approved';
          if (it.decision === 'DENY') initial[it.item_id] = 'rejected';
        }
        setDecision(initial);

        // 2) Load patient details (SSN from vault inside /patients/:patientId)
        const patientId = woJson.workOrder.patient_id;

        const pRes = await fetchWithAuth(
          getAccessTokenSilently,
          `${API_BASE}/api/patients/${encodeURIComponent(patientId)}`
        );

        const pText = await pRes.text();
        let pJson: any = null;
        try {
          pJson = pText ? JSON.parse(pText) : null;
        } catch {
          pJson = null;
        }

        if (!mounted) return;

        if (pRes.ok && pJson) {
          const ok = pJson as PatientDetailsOk;
          setPatientName(ok.name || '—');
          setPatientSsn(ok.ssn || '—');
          setPatientPharmacy(ok.pharmacy || '—');
        } else {
          const maybe = (pJson ?? {}) as PatientDetailsMaybe404;
          setPatientName('—');
          setPatientSsn(maybe.ssn || '—');
          setPatientPharmacy('—');
        }
      } catch (e: any) {
        console.error(e);
        if (mounted) setErrMsg(e?.message || 'Villa kom upp');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [workOrderId, getAccessTokenSilently]);

  // Fetch rejection reasons once
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const url = `${API_BASE}/api/rejection-reasons`;

        const res = await fetchWithAuth(getAccessTokenSilently, url);
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const json = await res.json();

        // Accept either { rows: [...] } or [...]
        const rows = Array.isArray(json) ? json : Array.isArray(json?.rows) ? json.rows : null;

        const finalList = (rows ?? FALLBACK_REASONS)
          .slice()
          .sort(
            (a: RejectionReason, b: RejectionReason) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
          );

        if (mounted) setReasons(finalList);
      } catch (e) {
        console.error('Failed to load rejection reasons, using fallback:', e);
        if (mounted)
          setReasons(
            FALLBACK_REASONS.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          );
      }
    })();

    return () => {
      mounted = false;
    };
  }, [getAccessTokenSilently]);

  const actionableItems = useMemo(() => items, [items]);

  const allDecided =
    actionableItems.length > 0 && actionableItems.every((it) => !!decision[it.item_id]);

  const kindLabel = (a: ItemAction) => {
    if (a === 'ADD') return 'Nýtt lyf';
    if (a === 'EDIT') return 'Breyting á lyfi';
    if (a === 'REMOVE') return 'Lyf tekið úr skömmtun';
    if (a === 'PAUSE') return 'Stöðva skömmtun';
    if (a === 'RESUME') return 'Endurvekja skömmtun';
    return a;
  };

  const postItemReview = async (params: {
    itemId: string;
    decision: 'APPROVE' | 'DENY';
    reasonCode?: string;
    notesPlaintext?: string;
  }) => {
    const body =
      params.decision === 'DENY'
        ? {
            decision: 'DENY',
            reasonCode: params.reasonCode ?? 'OTHER',
            notesPlaintext: params.notesPlaintext ?? null,
          }
        : { decision: 'APPROVE' };

    const res = await fetchWithAuth(
      getAccessTokenSilently,
      `${API_BASE}/api/work-order-items/${encodeURIComponent(params.itemId)}/reviews`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Review API ${res.status}: ${text}`);
    }
  };

  const handleApprove = async (itemId: string) => {
    setSubmittingItemId(itemId);
    try {
      await postItemReview({ itemId, decision: 'APPROVE' });
      setDecision((prev) => ({ ...prev, [itemId]: 'approved' }));
      setExpanded((prev) => ({ ...prev, [itemId]: false }));
    } finally {
      setSubmittingItemId(null);
    }
  };

  const openRejectModal = (itemId: string) => {
    setRejectingItemId(itemId);
    setRejectReasonCode('');
    setRejectNotes('');
  };

  const handleConfirmReject = async () => {
    if (!rejectingItemId) return;

    if (!rejectReasonCode) {
      alert('Vinsamlegast veldu ástæðu höfnunar.');
      return;
    }

    if (rejectReasonCode === 'OTHER' && !rejectNotes.trim()) {
      alert('Vinsamlegast útskýrðu stuttlega (Annað).');
      return;
    }

    const itemId = rejectingItemId;
    setSubmittingItemId(itemId);

    try {
      await postItemReview({
        itemId,
        decision: 'DENY',
        reasonCode: rejectReasonCode,
        notesPlaintext: rejectNotes.trim() || undefined,
      });

      setDecision((prev) => ({ ...prev, [itemId]: 'rejected' }));
      setExpanded((prev) => ({ ...prev, [itemId]: false }));
      setRejectingItemId(null);
      setRejectReasonCode('');
      setRejectNotes('');
    } finally {
      setSubmittingItemId(null);
    }
  };

  const handleDone = () => {
    navigate('/pharmacist');
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className="dashboard-main">
          <Background>Hleð…</Background>
        </main>
      </>
    );
  }

  return (
    <div className="dashboard">
      <Header />

      <main className="dashboard-main">
        <Background>
          <div className="pharm-patient-head">
            <div className="pp-name">{patientName}</div>
            <div className="pp-ssn">kt. {patientSsn}</div>
            <div className="pp-pharm">Verslun: {patientPharmacy}</div>
          </div>

          {errMsg && <div style={{ color: '#fff', marginBottom: 8 }}>{errMsg}</div>}

          <div className="pp-list">
            {items.map((it) => {
              const id = it.item_id;
              const isExpanded = !!expanded[id];
              const dec = decision[id];

              let rowClass = 'pp-row';
              if (it.action === 'ADD') rowClass += ' new';
              else if (it.action === 'EDIT') rowClass += ' edit';
              else if (it.action === 'REMOVE') rowClass += ' remove';

              if (dec === 'approved') rowClass += ' approved';
              if (dec === 'rejected') rowClass += ' rejected';

              const instruction =
                it.proposed_payload?.instruction ||
                it.proposed_payload?.proposedInstruction ||
                it.proposed_payload?.note ||
                '';

              return (
                <div key={id} className={rowClass}>
                  <button
                    className="pp-row-head"
                    onClick={() => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))}
                  >
                    <span className="pp-row-title">
                      <span className="pp-row-med-name">{it.medication_identifier}</span>

                      {!dec && <span className="pp-badge">{kindLabel(it.action)}</span>}

                      {dec === 'approved' && (
                        <span className="pp-decision-chip ok">
                          <span className="pp-decision-icon">✔</span>
                          Breyting staðfest
                        </span>
                      )}
                      {dec === 'rejected' && (
                        <span className="pp-decision-chip nok">
                          <span className="pp-decision-icon">&times;</span>
                          Breytingu hafnað
                        </span>
                      )}
                    </span>

                    <span className="pp-time-cell">{''}</span>
                  </button>

                  {isExpanded && (
                    <div className="pp-row-body">
                      <div className="pp-row-line">
                        <span className="pp-row-label">Tegund breytingar</span>
                        <span className="pp-chip light">{kindLabel(it.action)}</span>
                      </div>

                      {!!instruction && (
                        <div className="pp-row-line">
                          <span className="pp-row-label">Lyfjafyrirmæli</span>
                          <span className="pp-chip light">{instruction}</span>
                        </div>
                      )}

                      {dec === 'rejected' && it.notesPlaintext && (
                        <div className="pp-row-line">
                          <span className="pp-row-label">Athugasemd</span>
                          <span className="pp-chip light">{it.notesPlaintext}</span>
                        </div>
                      )}

                      <div className="pp-row-actions">
                        <button
                          className={`pp-btn ghost ${dec === 'rejected' ? 'is-active' : ''}`}
                          disabled={submittingItemId === id}
                          onClick={(e) => {
                            e.stopPropagation();
                            openRejectModal(id);
                          }}
                        >
                          Hafna
                        </button>
                        <button
                          className={`pp-btn primary ${dec === 'approved' ? 'is-active' : ''}`}
                          disabled={submittingItemId === id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprove(id);
                          }}
                        >
                          Staðfesta
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {allDecided && (
            <div className="pp-finalize-wrap">
              <button className="pp-finalize-btn" onClick={handleDone}>
                Til baka í biðröð
              </button>
            </div>
          )}

          {/* Reject modal with dropdown */}
          {rejectingItemId && (
            <div className="pp-modal-backdrop">
              <div className="pp-modal">
                <h2 className="pp-modal-title">Tillögu hafnað</h2>

                <div className="pp-modal-field">
                  <div className="pp-modal-label">Veldu ástæðu höfnunar</div>

                  <select
                    className="pp-modal-input"
                    value={rejectReasonCode}
                    onChange={(e) => setRejectReasonCode(e.target.value)}
                    disabled={submittingItemId === rejectingItemId}
                  >
                    <option value="" disabled>
                      — Veldu ástæðu —
                    </option>
                    {reasons.map((r) => (
                      <option key={r.reason_code} value={r.reason_code}>
                        {r.display_label}
                      </option>
                    ))}
                  </select>
                </div>

                {rejectReasonCode && (
                  <div className="pp-modal-field">
                    <div className="pp-modal-label">
                      Útskýrðu stuttlega {'·'}
                      {rejectReasonCode === 'OTHER' ? '(skyldugt)' : '(valfrjálst)'}
                    </div>
                    <input
                      type="text"
                      className="pp-modal-input"
                      value={rejectNotes}
                      onChange={(e) => setRejectNotes(e.target.value)}
                      placeholder={rejectReasonCode === 'OTHER' ? 'Útskýring...' : 'Valfrjálst…'}
                      disabled={submittingItemId === rejectingItemId}
                    />
                  </div>
                )}

                <div className="pp-modal-actions">
                  <button
                    className="pp-btn ghost"
                    onClick={() => {
                      setRejectingItemId(null);
                      setRejectReasonCode('');
                      setRejectNotes('');
                    }}
                    disabled={submittingItemId === rejectingItemId}
                  >
                    Hætta við
                  </button>
                  <button
                    className="pp-btn primary"
                    onClick={handleConfirmReject}
                    disabled={submittingItemId !== null}
                  >
                    Staðfesta
                  </button>
                </div>
              </div>
            </div>
          )}
        </Background>
      </main>
    </div>
  );
}
